import React, { useState, useEffect, useCallback } from 'react';
import './Style/AllUsers.css';
import TopNavbar from '../home/TopNavbar';
import Loading from '../home/Loading';
import { API_CONFIG } from './../../config';

const CREATE_USER_ENDPOINT = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.USERAPPINSTALL}`;

// Unified, robust date formatter for IST
const formatToIST = (dateString) => {
    if (!dateString) return 'Loading...';
    const date = new Date(dateString);
    if (isNaN(date)) return dateString;

    // If your backend stores UTC and you need to add 5 hours 30 mins:
    const istTime = new Date(date.getTime() + (5.5 * 60 * 60 * 1000));

    return istTime.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
    });
};

const AppInstallUsers = () => {
    const [users, setUsers] = useState([]);
    const [totalUsers, setTotalUsers] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Pagination states
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    /* =========================================
       FETCH USERS
    ========================================= */
    const fetchUsers = useCallback(async () => {
        try {
            const response = await fetch(CREATE_USER_ENDPOINT);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (data.success) {
                setUsers(data.data || []);
                setTotalUsers(data.count || 0);
                setLoading(false);
            } else {
                throw new Error(data.message || 'Failed to fetch users');
            }
        } catch (err) {
            setError('Failed to fetch users: ' + err.message);
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    /* =========================================
       PRINT
    ========================================= */
    const handlePrint = () => {
        window.print();
    };

    /* =========================================
       LOADING & ERROR STATES
    ========================================= */
    if (loading) {
        return <Loading />;
    }

    if (error) {
        return (
            <div className="all-user-data-container">
                <div className="user-data-content-wrapper">
                    <TopNavbar name="App Install Users" />
                    <div className="user-data-card">
                        <div className="app-install-error">
                            <h2>{error}</h2>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    /* =========================================
       PAGINATION LOGIC
    ========================================= */
    const totalItems = users.length;
    const totalPages = itemsPerPage === 'all' ? 1 : Math.ceil(totalItems / Number(itemsPerPage));
    const startIndex = itemsPerPage === 'all' ? 0 : (currentPage - 1) * Number(itemsPerPage);
    
    const displayedUsers = itemsPerPage === 'all'
        ? users
        : users.slice(startIndex, startIndex + Number(itemsPerPage));

    const handleItemsPerPage = (e) => {
        setItemsPerPage(e.target.value);
        setCurrentPage(1);
    };

    const goToPage = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    return (
        <div className="all-user-data-container">
            <div className="user-data-content-wrapper">
                
                {/* TOTAL USERS BANNER */}
                <div className="app-install-total-banner">
                    <h2>Total Registered Users: {totalUsers}</h2>
                </div>

                {/* TABLE CARD */}
                <div className="user-data-card">
                    <h2 className="user-data-title">App Install Users</h2>

                    {/* TOP CONTROLS */}
                    <div className="app-install-controls">
                        <div className="items-per-page">
                            <label htmlFor="itemsPerPage">Show:</label>
                            <select id="itemsPerPage" value={itemsPerPage} onChange={handleItemsPerPage}>
                                <option value="10">10</option>
                                <option value="25">25</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                                <option value="all">All</option>
                            </select>
                            <span>items</span>
                        </div>

                        <button className="app-install-print-button no-print" onClick={handlePrint}>
                            🖨 Print Report
                        </button>
                    </div>

                    {/* TABLE */}
                    <div className="table-responsive">
                        <table className="user-data-table">
                            <thead>
                                <tr>
                                    <th>SL</th>
                                    <th>User ID</th>
                                    <th>Registration Time</th>
                                </tr>
                            </thead>
                            <tbody>
                                {displayedUsers.length > 0 ? (
                                    displayedUsers.map((user, index) => {
                                        return (
                                            <tr key={user.id || index}>
                                                <td>{startIndex + index + 1}</td>
                                                <td className="app-install-user-id">
                                                    {user.userId || 'N/A'}
                                                </td>
                                                <td>
                                                    {formatToIST(user.date)}
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="3" className="no-data-message">
                                            No users found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* PAGINATION */}
                    {itemsPerPage !== 'all' && totalItems > 0 && (
                        <div className="user-pagination">
                            <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}>
                                ← Previous
                            </button>
                            <span>
                                Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
                            </span>
                            <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}>
                                Next →
                            </button>
                        </div>
                    )}

                    {/* RECORD INFORMATION */}
                    <div className="user-data-stats">
                        <p>
                            Showing <strong>{totalItems === 0 ? 0 : startIndex + 1}</strong> to{' '}
                            <strong>{Math.min(startIndex + displayedUsers.length, totalItems)}</strong> of{' '}
                            <strong>{totalItems}</strong> users
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AppInstallUsers;